import React, { useState } from 'react';
import { CommandCenterLayout } from '../../components/layout/CommandCenterLayout';
import { KPICard } from '../../components/ui/KPICard';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { DataTable } from '../../components/ui/DataTable';
import { StatusBadge } from '../../components/ui/StatusBadge';
import { Calendar, MapPin, Users, Recycle, Plus, Filter, Search, Clock, CheckCircle2, XCircle, FileText, Upload } from 'lucide-react';
import { Input } from '../../components/ui/Input';
import { Modal } from '../../components/ui/Modal';
import { Select } from '../../components/ui/Select';
import { Textarea } from '../../components/ui/Textarea';
// Mock Data
interface EventItem {
  id: string;
  name: string;
  type: string;
  date: string;
  location: string;
  participants: number;
  tonnage: number;
  status: 'upcoming' | 'ongoing' | 'completed' | 'pending';
  description?: string;
  coordinator?: string;
  juknisUrl?: string;
}
const mockEvents: EventItem[] = [{
  id: '1',
  name: 'HPSN 2025: Atasi Sampah Plastik',
  type: 'HPSN',
  date: '21 Feb 2025',
  location: 'Nasional (34 Provinsi)',
  participants: 125000,
  tonnage: 450,
  status: 'ongoing',
  description: 'Peringatan Hari Peduli Sampah Nasional 2025.',
  coordinator: 'Direktorat PSLB3'
}, {
  id: '2',
  name: 'Mega Clean-Up Ciliwung',
  type: 'Mega Clean-Up',
  date: '15 Mar 2025',
  location: 'DKI Jakarta & Jawa Barat',
  participants: 5000,
  tonnage: 0,
  status: 'upcoming',
  description: 'Aksi bersih sungai Ciliwung.',
  coordinator: 'Budi Santoso'
}, {
  id: '3',
  name: 'Business Matching Sampah',
  type: 'Business Matching',
  date: '05 Jun 2025',
  location: 'Surabaya',
  participants: 0,
  tonnage: 0,
  status: 'upcoming',
  description: 'Temu bisnis pengelola sampah dan off-taker.',
  coordinator: 'DLH Surabaya'
}];
const pendingLocations = [{
  id: 1,
  event: 'HPSN 2025',
  region: 'Kab. Garut',
  pic: 'Asep Surasep',
  status: 'pending'
}, {
  id: 2,
  event: 'HPSN 2025',
  region: 'Kota Tasikmalaya',
  pic: 'Budi Budiman',
  status: 'pending'
}, {
  id: 3,
  event: 'Mega Clean-Up Ciliwung',
  region: 'Kota Depok',
  pic: 'Cici Suci',
  status: 'pending'
}];
export function EventNasionalPage() {
  const [activeTab, setActiveTab] = useState('daftar');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedEvent, setSelectedEvent] = useState<EventItem | null>(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<any | null>(null);
  const [approvalNote, setApprovalNote] = useState('');
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'ongoing':
        return <StatusBadge status="hijau" data-id="element-1304">Berlangsung</StatusBadge>;
      case 'upcoming':
        return <StatusBadge status="biru" data-id="element-1305">Akan Datang</StatusBadge>;
      case 'completed':
        return <StatusBadge status="abu" data-id="element-1306">Selesai</StatusBadge>;
      case 'pending':
        return <StatusBadge status="kuning" data-id="element-1307">Perlu Approval</StatusBadge>;
      default:
        return null;
    }
  };
  const columns = [{
    header: 'Nama Event',
    accessorKey: 'name' as keyof EventItem,
    cell: (item: EventItem) => <div data-id="element-1308">
          <div className="font-medium text-gray-900" data-id="element-1309">{item.name}</div>
          <div className="text-xs text-gray-500" data-id="element-1310">{item.type}</div>
        </div>
  }, {
    header: 'Tanggal',
    accessorKey: 'date' as keyof EventItem,
    cell: (item: EventItem) => <span className="text-gray-600" data-id="element-1311">{item.date}</span>
  }, {
    header: 'Lokasi',
    accessorKey: 'location' as keyof EventItem,
    cell: (item: EventItem) => <div className="flex items-center gap-1 text-gray-600" data-id="element-1312">
          <MapPin className="h-3 w-3" data-id="element-1313" />
          {item.location}
        </div>
  }, {
    header: 'Peserta',
    accessorKey: 'participants' as keyof EventItem,
    cell: (item: EventItem) => <span className="text-gray-600" data-id="element-1314">
          {item.participants.toLocaleString()}
        </span>
  }, {
    header: 'Status',
    cell: (item: EventItem) => getStatusBadge(item.status)
  }, {
    header: 'Aksi',
    cell: (item: EventItem) => <Button variant="ghost" size="sm" className="text-primary-600 hover:text-primary-700" onClick={() => setSelectedEvent(item)} data-id="element-1315">
          Detail
        </Button>
  }];
  const locationColumns = [{
    header: 'Event',
    accessorKey: 'event'
  }, {
    header: 'Wilayah',
    accessorKey: 'region'
  }, {
    header: 'PIC Daerah',
    accessorKey: 'pic'
  }, {
    header: 'Status',
    cell: () => <StatusBadge status="kuning" data-id="element-1316">Menunggu</StatusBadge>
  }, {
    header: 'Aksi',
    cell: (item: any) => <Button size="sm" variant="outline" onClick={() => setSelectedLocation(item)} data-id="element-1317">
          Review
        </Button>
  }];
  return <CommandCenterLayout title="Event Nasional" subtitle="Orkestrasi event lingkungan hidup dan approval lokasi" data-id="element-1318">
      {/* KPI Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8" data-id="element-1319">
        <KPICard title="Total Event" value="1,240" icon={Calendar} trend={{
        value: '12% vs tahun lalu',
        direction: 'up'
      }} data-id="element-1320" />
        <KPICard title="Lokasi Terdaftar" value="5,892" icon={MapPin} trend={{
        value: '85 baru bulan ini',
        direction: 'up'
      }} data-id="element-1321" />
        <KPICard title="Total Peserta" value="2.4M" icon={Users} description="Relawan & Masyarakat" data-id="element-1322" />
        <KPICard title="Total Tonase" value="15.2K" icon={Recycle} description="Ton Sampah Terkelola" data-id="element-1323" />
      </div>

      {/* Live Dashboard for Ongoing Event */}
      <Card className="mb-8 border-l-4 border-l-green-500 bg-green-50/30" data-id="element-1324">
        <CardHeader data-id="element-1325">
          <div className="flex justify-between items-start" data-id="element-1326">
            <div data-id="element-1327">
              <div className="flex items-center gap-2 mb-1" data-id="element-1328">
                <span className="animate-pulse h-2 w-2 rounded-full bg-green-600" data-id="element-1329"></span>
                <span className="text-xs font-bold text-green-700 uppercase tracking-wider" data-id="element-1330">
                  Sedang Berlangsung
                </span>
              </div>
              <CardTitle className="text-xl text-gray-900" data-id="element-1331">
                HPSN 2025: Atasi Sampah Plastik
              </CardTitle>
              <CardDescription data-id="element-1332">
                21 Februari 2025 • Skala Nasional
              </CardDescription>
            </div>
            <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white" data-id="element-1333">
              Lihat Live Dashboard
            </Button>
          </div>
        </CardHeader>
        <CardContent data-id="element-1334">
          <div className="grid grid-cols-4 gap-4 text-center" data-id="element-1335">
            <div className="p-4 bg-white rounded-lg shadow-sm border border-green-100" data-id="element-1336">
              <div className="text-2xl font-bold text-gray-900" data-id="element-1337">34</div>
              <div className="text-xs text-gray-500 uppercase" data-id="element-1338">
                Provinsi Aktif
              </div>
            </div>
            <div className="p-4 bg-white rounded-lg shadow-sm border border-green-100" data-id="element-1339">
              <div className="text-2xl font-bold text-gray-900" data-id="element-1340">125K</div>
              <div className="text-xs text-gray-500 uppercase" data-id="element-1341">
                Peserta Check-in
              </div>
            </div>
            <div className="p-4 bg-white rounded-lg shadow-sm border border-green-100" data-id="element-1342">
              <div className="text-2xl font-bold text-gray-900" data-id="element-1343">450</div>
              <div className="text-xs text-gray-500 uppercase" data-id="element-1344">
                Ton Sampah (Live)
              </div>
            </div>
            <div className="p-4 bg-white rounded-lg shadow-sm border border-green-100" data-id="element-1345">
              <div className="text-2xl font-bold text-gray-900" data-id="element-1346">12</div>
              <div className="text-xs text-gray-500 uppercase" data-id="element-1347">
                Lokasi Baru (+1h)
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Main Content */}
      <Card data-id="element-1348">
        <CardHeader className="flex flex-row items-center justify-between pb-4" data-id="element-1349">
          <div data-id="element-1350">
            <CardTitle data-id="element-1351">Manajemen Event</CardTitle>
            <CardDescription data-id="element-1352">
              Kelola event nasional dan persetujuan lokasi
            </CardDescription>
          </div>
          <Button className="bg-primary-600 hover:bg-primary-700 text-white gap-2" onClick={() => setIsCreateModalOpen(true)} data-id="element-1353">
            <Plus className="h-4 w-4" data-id="element-1354" />
            Buat Event Baru
          </Button>
        </CardHeader>
        <CardContent data-id="element-1355">
          {/* Tabs */}
          <div className="flex flex-col gap-4 mb-6" data-id="element-1356">
            <div className="flex border-b" data-id="element-1357">
              <button onClick={() => setActiveTab('daftar')} className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${activeTab === 'daftar' ? 'border-primary-600 text-primary-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`} data-id="element-1358">
                Daftar Event
              </button>
              <button onClick={() => setActiveTab('approval')} className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${activeTab === 'approval' ? 'border-primary-600 text-primary-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`} data-id="element-1359">
                Approval Lokasi{' '}
                <span className="ml-1 bg-red-100 text-red-600 text-xs px-1.5 py-0.5 rounded-full" data-id="element-1360">
                  3
                </span>
              </button>
            </div>

            {activeTab === 'daftar' && <>
                <div className="flex gap-4" data-id="element-1361">
                  <div className="relative flex-1" data-id="element-1362">
                    <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" data-id="element-1363" />
                    <Input placeholder="Cari nama event atau lokasi..." className="pl-9" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-id="element-1364" />
                  </div>
                  <Button variant="outline" className="gap-2" data-id="element-1365">
                    <Filter className="h-4 w-4" data-id="element-1366" />
                    Filter
                  </Button>
                </div>
                <DataTable data={mockEvents} columns={columns} keyExtractor={item => item.id} data-id="element-1367" />
              </>}

            {activeTab === 'approval' && <DataTable data={pendingLocations} columns={locationColumns} keyExtractor={item => item.id} data-id="element-1368" />}
          </div>
        </CardContent>
      </Card>

      {/* Create Event Modal */}
      <Modal isOpen={isCreateModalOpen} onClose={() => setIsCreateModalOpen(false)} title="Buat Event Baru" description="Jadwalkan event lingkungan hidup baru" size="lg" footer={<div className="flex justify-end gap-2 w-full" data-id="element-1370">
            <Button variant="outline" onClick={() => setIsCreateModalOpen(false)} data-id="element-1371">
              Batal
            </Button>
            <Button className="bg-primary-600 text-white" data-id="element-1372">Buat Event</Button>
          </div>} data-id="element-1369">
        <div className="space-y-4" data-id="element-1373">
          <div className="grid grid-cols-2 gap-4" data-id="element-1374">
            <div className="col-span-2" data-id="element-1375">
              <label className="text-sm font-medium text-gray-700 mb-1 block" data-id="element-1376">
                Nama Event
              </label>
              <Input placeholder="Contoh: Aksi Bersih Pantai 2024" data-id="element-1377" />
            </div>

            <div data-id="element-1378">
              <label className="text-sm font-medium text-gray-700 mb-1 block" data-id="element-1379">
                Tipe Event
              </label>
              <Select data-id="element-1380">
                <option value="HPSN" data-id="element-1381">HPSN</option>
                <option value="Mega Clean-Up" data-id="element-1382">Mega Clean-Up</option>
                <option value="Business Matching" data-id="element-1383">Business Matching</option>
                <option value="Lainnya" data-id="element-1384">Lainnya</option>
              </Select>
            </div>

            <div data-id="element-1385">
              <label className="text-sm font-medium text-gray-700 mb-1 block" data-id="element-1386">
                Tanggal Mulai
              </label>
              <Input type="date" data-id="element-1387" />
            </div>

            <div className="col-span-2" data-id="element-1388">
              <label className="text-sm font-medium text-gray-700 mb-1 block" data-id="element-1389">
                Lokasi
              </label>
              <Select data-id="element-1390">
                <option data-id="element-1391">Nasional (Seluruh Indonesia)</option>
                <option data-id="element-1392">Provinsi Tertentu</option>
                <option data-id="element-1393">Kota/Kabupaten Tertentu</option>
              </Select>
            </div>

            <div className="col-span-2" data-id="element-1394">
              <label className="text-sm font-medium text-gray-700 mb-1 block" data-id="element-1395">
                Deskripsi
              </label>
              <Textarea placeholder="Jelaskan detail kegiatan..." className="h-24" data-id="element-1396" />
            </div>

            <div className="col-span-2" data-id="element-1397">
              <label className="text-sm font-medium text-gray-700 mb-1 block" data-id="element-1398">
                Upload Juknis (PDF)
              </label>
              <div className="flex gap-2" data-id="element-1399">
                <Input type="file" data-id="element-1400" />
                <Button variant="outline" data-id="element-1401">
                  <Upload className="h-4 w-4" data-id="element-1402" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </Modal>

      {/* Location Approval Modal */}
      <Modal isOpen={!!selectedLocation} onClose={() => setSelectedLocation(null)} title="Review Lokasi Event" size="lg" footer={<div className="flex justify-end gap-2 w-full" data-id="element-1404">
            <Button variant="outline" onClick={() => setSelectedLocation(null)} data-id="element-1405">
              Batal
            </Button>
            <Button variant="destructive" data-id="element-1406">Tolak</Button>
            <Button className="bg-green-600 text-white" data-id="element-1407">Approve Lokasi</Button>
          </div>} data-id="element-1403">
        {selectedLocation && <div className="space-y-4" data-id="element-1408">
            <div className="grid grid-cols-2 gap-4" data-id="element-1409">
              <div className="p-3 bg-gray-50 rounded border" data-id="element-1410">
                <p className="text-xs text-gray-500" data-id="element-1411">Event</p>
                <p className="font-medium" data-id="element-1412">{selectedLocation.event}</p>
              </div>
              <div className="p-3 bg-gray-50 rounded border" data-id="element-1413">
                <p className="text-xs text-gray-500" data-id="element-1414">Wilayah</p>
                <p className="font-medium" data-id="element-1415">{selectedLocation.region}</p>
              </div>
            </div>
            <div className="h-48 bg-gray-100 rounded flex items-center justify-center border" data-id="element-1416">
              <p className="text-gray-400 flex items-center gap-2" data-id="element-1417">
                <MapPin data-id="element-1418" /> Peta Lokasi
              </p>
            </div>
            <div data-id="element-1419">
              <label className="text-sm font-medium text-gray-700 mb-1 block" data-id="element-1420">
                Catatan Approval
              </label>
              <Textarea placeholder="Tambahkan catatan untuk daerah..." value={approvalNote} onChange={e => setApprovalNote(e.target.value)} data-id="element-1421" />
            </div>
          </div>}
      </Modal>
    </CommandCenterLayout>;
}